<template>
  <section class="page" data-testid="page-attitudes">
    <header class="page-header">
      <div><h1>态度</h1><p>按用户和人设维护交互态度、额外提示词与自定义类型。</p></div>
      <button class="button primary" :disabled="loading" @click="load">{{ loading ? "刷新中..." : "刷新" }}</button>
    </header>
    <div class="metrics-grid">
      <MetricCard label="态度记录" :value="attitudeRows.length" />
      <MetricCard label="当前匹配" :value="filteredAttitudeRows.length" />
      <MetricCard label="态度类型" :value="typeRows.length" />
      <MetricCard label="自定义类型" :value="customTypeCount" />
      <MetricCard label="人设范围" :value="filterPersona || '全局'" />
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>

    <div class="split">
      <div class="panel">
        <h2>态度记录</h2>
        <div class="toolbar">
          <select v-model="filterPersona" @change="load">
            <option value="">全局</option>
            <option v-for="item in personas" :key="item" :value="item">{{ item }}</option>
          </select>
          <input v-model="query" placeholder="搜索 QQ、类型、提示词" />
          <select v-model="typeFilter">
            <option value="">全部类型</option>
            <option v-for="(item, key) in types" :key="key" :value="key">{{ item.label || key }}</option>
          </select>
        </div>
        <DataTable :columns="columns" :rows="filteredAttitudeRows" row-key="user_id">
          <template #type="{ row }"><StatusBadge status="hot">{{ typeLabel(row.type) }}</StatusBadge></template>
          <template #extra_prompt="{ row }"><pre>{{ row.extra_prompt }}</pre></template>
          <template #actions="{ row }">
            <button class="button small" :disabled="loading" @click="editAttitude(row)">编辑</button>
            <button class="button small danger" :disabled="loading" @click="deleteAttitude(row.user_id)">删除</button>
          </template>
        </DataTable>
      </div>

      <div>
        <div class="panel">
          <h2>设置态度</h2>
          <div class="form-grid">
            <FormField label="用户 QQ"><input v-model="form.user_id" /></FormField>
            <FormField label="人设">
              <select v-model="formPersonaSelection" @change="applyFormPersonaSelection">
                <option value="">全局</option>
                <option v-for="item in personas" :key="item" :value="item">{{ item }}</option>
                <option value="__custom">自定义</option>
              </select>
            </FormField>
            <FormField v-if="formPersonaSelection === '__custom'" label="自定义人设"><input v-model="form.persona" /></FormField>
            <FormField label="类型">
              <select v-model="form.type">
                <option v-for="(item, key) in types" :key="key" :value="key">{{ item.label || key }}</option>
              </select>
            </FormField>
          </div>
          <FormField label="额外 Prompt"><textarea v-model="form.extra_prompt" /></FormField>
          <div class="actions">
            <button class="button primary" :disabled="loading || !canSaveAttitude" @click="saveAttitude">{{ loading ? "处理中..." : "保存态度" }}</button>
            <button class="button" @click="resetAttitudeForm">清空</button>
          </div>
        </div>

        <div class="panel">
          <h2>自定义态度类型</h2>
          <div class="form-grid">
            <FormField label="ID"><input v-model="typeForm.id" /></FormField>
            <FormField label="标签"><input v-model="typeForm.label" /></FormField>
          </div>
          <FormField label="描述"><input v-model="typeForm.description" /></FormField>
          <FormField label="Prompt Addon"><textarea v-model="typeForm.prompt_addon" /></FormField>
          <div class="actions">
            <button class="button primary" :disabled="loading || !canSaveType" @click="createType">{{ typeEditing ? "保存类型" : "创建类型" }}</button>
            <button class="button" @click="resetTypeForm">清空</button>
          </div>
          <DataTable :columns="typeColumns" :rows="typeRows" row-key="id">
            <template #builtin="{ row }"><StatusBadge :status="row.builtin ? 'ok' : 'hot'">{{ row.builtin ? "内置" : "自定义" }}</StatusBadge></template>
            <template #prompt_addon="{ row }"><pre>{{ row.prompt_addon || "-" }}</pre></template>
            <template #actions="{ row }">
              <button v-if="!row.builtin" class="button small" :disabled="loading" @click="editType(row)">编辑</button>
              <button v-if="!row.builtin" class="button small danger" :disabled="loading" @click="deleteType(row.id)">删除</button>
            </template>
          </DataTable>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { api, postJson } from "../api/client";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";
import StatusBadge from "../ui/StatusBadge.vue";

const toast = useToastStore();
const loading = ref(false);
const error = ref("");
const actionResult = ref("");
const filterPersona = ref("");
const query = ref("");
const typeFilter = ref("");
const attitudes = ref<Record<string, any>>({});
const types = ref<Record<string, any>>({});
const personas = ref<string[]>([]);
const formPersonaSelection = ref("");
const form = reactive({ user_id: "", persona: "", type: "acquaintance", extra_prompt: "" });
const typeForm = reactive({ id: "", label: "", description: "", prompt_addon: "" });
const typeEditing = ref(false);
const columns = [
  { key: "user_id", label: "用户" },
  { key: "type", label: "类型" },
  { key: "interaction_count", label: "交互" },
  { key: "extra_prompt", label: "提示词" },
  { key: "actions", label: "操作" }
];
const typeColumns = [
  { key: "id", label: "ID" },
  { key: "label", label: "标签" },
  { key: "builtin", label: "来源" },
  { key: "prompt_addon", label: "Prompt" },
  { key: "actions", label: "操作" }
];
const attitudeRows = computed(() => Object.entries(attitudes.value).map(([user_id, item]) => ({ user_id, ...item })));
const typeRows = computed(() => Object.entries(types.value).map(([id, item]) => ({ id, ...item })));
const customTypeCount = computed(() => typeRows.value.filter((row) => !row.builtin).length);
const canSaveAttitude = computed(() => Boolean(form.user_id && form.type));
const canSaveType = computed(() => Boolean(typeForm.id.trim() && typeForm.label.trim() && typeForm.prompt_addon.trim()));
const filteredAttitudeRows = computed(() => {
  const text = query.value.trim().toLowerCase();
  return attitudeRows.value.filter((row) => {
    if (typeFilter.value && row.type !== typeFilter.value) return false;
    if (!text) return true;
    return `${row.user_id} ${row.type} ${typeLabel(row.type)} ${row.extra_prompt || ""}`.toLowerCase().includes(text);
  });
});

function typeLabel(type: string) {
  return types.value[type]?.label || type || "-";
}

function syncFormPersonaSelection() {
  if (!form.persona) {
    formPersonaSelection.value = "";
  } else if (personas.value.includes(form.persona)) {
    formPersonaSelection.value = form.persona;
  } else {
    formPersonaSelection.value = "__custom";
  }
}
function applyFormPersonaSelection() {
  if (formPersonaSelection.value === "__custom") return;
  form.persona = formPersonaSelection.value;
}
function errorText(err: unknown, fallback: string) {
  return err instanceof Error ? err.message : fallback;
}
async function runAction(label: string, operation: () => Promise<unknown>) {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await operation();
    actionResult.value = label;
    toast.push(label);
    await load();
  } catch (err) {
    error.value = errorText(err, `${label}失败`);
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const suffix = filterPersona.value ? `?persona=${encodeURIComponent(filterPersona.value)}` : "";
    attitudes.value = (await api<{ attitudes: Record<string, any> }>(`/api/attitudes${suffix}`)).attitudes || {};
    types.value = (await api<{ types: Record<string, any> }>("/api/attitude/types")).types || {};
    personas.value = (await api<{ personas: string[] }>("/api/attitude/personas")).personas || [];
    syncFormPersonaSelection();
  } catch (err) {
    error.value = errorText(err, "态度数据加载失败");
  } finally {
    loading.value = false;
  }
}
function editAttitude(row: any) {
  form.user_id = String(row.user_id || "");
  form.persona = filterPersona.value;
  form.type = row.type || "acquaintance";
  form.extra_prompt = row.extra_prompt || "";
  syncFormPersonaSelection();
}
function resetAttitudeForm() {
  Object.assign(form, { user_id: "", persona: "", type: "acquaintance", extra_prompt: "" });
  syncFormPersonaSelection();
}
async function saveAttitude() {
  if (!canSaveAttitude.value) return;
  await runAction("态度已保存", async () => {
    await postJson("/api/attitude/set", { ...form, user_id: Number(form.user_id), persona: form.persona || undefined });
    if (form.persona && !personas.value.includes(form.persona)) personas.value = [...personas.value, form.persona].sort();
  });
}
async function deleteAttitude(userId: string) {
  if (!window.confirm("确定删除这条态度记录？")) return;
  await runAction("态度已删除", () => api("/api/attitude", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ user_id: Number(userId), persona: filterPersona.value || undefined }) }));
}
function editType(row: any) {
  Object.assign(typeForm, {
    id: row.id,
    label: row.label || "",
    description: row.description || "",
    prompt_addon: row.prompt_addon || ""
  });
  typeEditing.value = true;
}
function resetTypeForm() {
  Object.assign(typeForm, { id: "", label: "", description: "", prompt_addon: "" });
  typeEditing.value = false;
}
async function createType() {
  if (!canSaveType.value) return;
  const label = typeEditing.value ? "类型已保存" : "类型已创建";
  await runAction(label, async () => {
    if (typeEditing.value) {
      await api(`/api/attitude/types/${encodeURIComponent(typeForm.id)}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(typeForm) });
    } else {
      await postJson("/api/attitude/types", typeForm);
    }
    resetTypeForm();
  });
}
async function deleteType(id: string) {
  if (!window.confirm(`确定删除态度类型 ${id}？`)) return;
  await runAction("类型已删除", () => api(`/api/attitude/types/${encodeURIComponent(id)}`, { method: "DELETE" }));
}
onMounted(load);
</script>

<style scoped>
.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}
</style>
